import React from 'react';
import {
	BaasicInput,
	BaasicTimeInput,
	BaasicHeader,
	Loader,
	BaasicCheckbox,
	SliderCheckBox,
	BaasicDropdown,
	CustomCreateableSelect,
} from 'core/components';
import { observer } from 'mobx-react';
import { translate } from 'core/utils';
import { defaultTemplate } from 'core/hoc';
import { warehouseManagementType } from 'common/constants';
import ReactTooltip from 'react-tooltip';

function WarehouseEditTemplate({ warehouseEditViewStore, t }) {
	const { form, loading, rootStore, isUpdateAction } = warehouseEditViewStore;
	if (loading) return <Loader />;

	return (
		<section>
			<ReactTooltip />
			<BaasicHeader
				title={isUpdateAction ? t('WAREHOUSEVIEW.EDIT') : t('WAREHOUSEVIEW.CREATE')}
				rootStore={rootStore}
			/>
			<form className="container container--lrg container--footer" onSubmit={form.onSubmit}>
				<div className="row spc--bottom--sml">
					<div className="col col-sml-12 col-med-6">
						<BaasicInput field={form.$('identifier')} />
					</div>
					<div className="col col-sml-12 col-med-6">
						<BaasicInput field={form.$('applicationTitle')} />
					</div>
				</div>

				<WarehouseAdditionalInfoSection viewStore={warehouseEditViewStore} t={t} />
				<WarehouseAddressSection viewStore={warehouseEditViewStore} t={t} />
				<WarehouseContactSection viewStore={warehouseEditViewStore} t={t} />
				<WarehouseCredentialsSection viewStore={warehouseEditViewStore} t={t} />
				<WarehouseAPISection viewStore={warehouseEditViewStore} t={t} />
				{form.error && (
					<React.Fragment>
						<p className="type--color--warning">{translate(form.error)}</p>
					</React.Fragment>
				)}
				<button className="btn btn--med btn--primary" type="submit" disabled={loading}>
					{loading ? (
						<div className="spinnercont spc--right--sml">
							<div className="spinner"></div>
						</div>
					) : null}
					{t('SAVE')}
				</button>
			</form>
		</section>
	);
}

const WarehouseCredentialsSection = observer(({ viewStore, t }) => {
	const { form, warehouseManagementTypeDropdownStore } = viewStore;
	return (
		<div className="spc--bottom--med">
			<h2 className="spc--bottom--sml">{t('WAREHOUSEVIEW.WAREHOUSEMANAGEMENT')}</h2>
			<div className="row">
				<div className="col col-sml-12 col-med-6 spc--bottom--sml">
					<BaasicDropdown
						dropdownStore={warehouseManagementTypeDropdownStore}
						isClearable={true}
						className="spc--right--sml"
					/>
				</div>
			</div>
			{form.$('warehouseOrganizationInfo.warehouseManagementTypeId').value && (
				<div className="row">
					<div className="col col-sml-12 col-med-6">
						<div className="spc--bottom--sml">
							<BaasicCheckbox field={form.$('sendOrderAPI')} />
						</div>
					</div>
					<div className="col col-sml-12 col-med-6">
						<div className="spc--bottom--sml">
							<BaasicCheckbox field={form.$('sendInventoryAPI')} />
						</div>
					</div>
					<div className="col col-sml-12 col-med-6">
						<div className="spc--bottom--sml">
							<BaasicCheckbox field={form.$('sendTransferAPI')} />
						</div>
					</div>
				</div>
			)}
			{form.$('warehouseOrganizationInfo.warehouseManagementTypeId').value && (
				<WMSCredentials {...{ warehouseManagementTypeDropdownStore, form }} />
			)}
		</div>
	);
});

const WMSCredentials = observer(({ warehouseManagementTypeDropdownStore, form }) => {
	switch (warehouseManagementTypeDropdownStore.value.extra.abrv) {
		case warehouseManagementType.daVinci:
			return <DaVinciCredentialsSection form={form} />;
		case warehouseManagementType.extensiv:
			return <ExtensivCredentialsSection form={form} />;
		default:
			return null;
	}
});

const ExtensivCredentialsSection = observer(({ form }) => {
	return (
		<div className="row">
			<div className="col col-sml-12 col-lrg-12 col-xxlrg-6">
				<BaasicInput field={form.$('warehouseOrganizationInfo.facilityId')} />
			</div>
			<div className="col col-sml-12 col-lrg-12 col-xxlrg-6">
				<BaasicInput field={form.$('warehouseOrganizationInfo.userLoginId')} />
			</div>
		</div>
	);
});

const DaVinciCredentialsSection = observer(({ form }) => {
	return (
		<div className="row">
			<div className="col col-sml-12 col-lrg-12 col-xxlrg-6">
				<BaasicInput field={form.$('warehouseOrganizationInfo.warehouseId')} />
			</div>
			<div className="col col-sml-12 col-lrg-12 col-xxlrg-6">
				<BaasicInput field={form.$('warehouseOrganizationInfo.dbName')} />
			</div>
			<div className="col col-sml-12 col-lrg-12 col-xxlrg-6">
				<BaasicInput field={form.$('warehouseOrganizationInfo.subdomain')} />
			</div>
			<div className="col col-sml-12 col-lrg-12 col-xxlrg-6">
				<BaasicInput field={form.$('warehouseOrganizationInfo.licenseId')} />
			</div>
			<div className="col col-sml-12 col-lrg-12 col-xxlrg-6">
				<BaasicInput field={form.$('warehouseOrganizationInfo.userName')} />
			</div>
			<div className="col col-sml-12 col-lrg-12 col-xxlrg-6">
				<BaasicInput field={form.$('warehouseOrganizationInfo.password')} />
			</div>
		</div>
	);
});

const WarehouseAPISection = observer(({ viewStore, t }) => {
	const { form, generateApiKey, isHavingAPIKey } = viewStore;
	return (
		<div className="spc--bottom--med">
			<h2 className="spc--bottom--sml">{t('WAREHOUSEVIEW.APISECTION')}</h2>
			<div className="inputgroup">
				<div className="inputgroup--main">
					<BaasicInput field={form.$('apiKey')} disabled />
				</div>
				{!isHavingAPIKey ? (
					<div className="inputgroup--aside align--v--bottom">
						<button
							className="btn btn--med btn--secondary spc--bottom--sml spc--left--tny"
							type="button"
							onClick={generateApiKey}
						>
							{t('GENERATEAPIKEY')}
						</button>
					</div>
				) : null}
			</div>
		</div>
	);
});

const WarehouseContactSection = observer(({ viewStore, t }) => {
	const { form, isUpdateAction } = viewStore;
	return (
		<div className="spc--bottom--sml">
			<h2 className="spc--bottom--sml">{t('WAREHOUSEVIEW.CONTACT')}</h2>

			<div className="row">
				<div className="col col-sml-12 col-lrg-12 col-xxlrg-6">
					<BaasicInput field={form.$('contact.email1')} />
				</div>
				<div className="col col-sml-12 col-lrg-12 col-xxlrg-6">
					<BaasicInput field={form.$('contact.phone1')} />
				</div>
				<div className="col col-sml-12 col-lrg-12 col-xxlrg-6">
					<BaasicInput field={form.$('contact.email2')} />
				</div>
				<div className="col col-sml-12 col-lrg-12 col-xxlrg-6">
					<BaasicInput field={form.$('contact.phone2')} />
				</div>
				{!isUpdateAction && (
					<React.Fragment>
						<div className="col col-sml-12">
							<div className="separator--primary spc--top--sml spc--bottom--sml"></div>
						</div>
						<div className="col col-sml-12">
							<BaasicInput field={form.$('email')} />
						</div>
						<div className="col col-sml-12 col-med-6">
							<BaasicInput field={form.$('password')} type="password" />
						</div>
						<div className="col col-sml-12 col-med-6">
							<BaasicInput field={form.$('confirmPassword')} type="password" />
						</div>
					</React.Fragment>
				)}
			</div>
		</div>
	);
});

const WarehouseAddressSection = observer(({ viewStore, t }) => {
	const { form, countryDropdownStore, stateDropdownStore, timezoneDropdownStore } = viewStore;
	return (
		<div className="spc--bottom--med">
			<h2 className="spc--bottom--sml">{t('WAREHOUSEVIEW.ADDRESS')}</h2>

			<div className="row spc--bottom--med">
				<div className="col col-sml-12 col-med-6">
					<BaasicInput field={form.$('address.address1')} />
				</div>
				<div className="col col-sml-12 col-med-6">
					<BaasicInput field={form.$('address.address2')} />
				</div>
				<div className="col col-sml-12 col-med-6">
					<BaasicInput field={form.$('address.address3')} />
				</div>
				<div className="col col-sml-12 col-med-6">
					<BaasicInput field={form.$('address.city')} />
				</div>
				<div className="col col-sml-12 col-med-6">
					<BaasicInput field={form.$('address.zipCode')} />
				</div>
				<div className="col col-sml-12 col-med-6">
					<BaasicDropdown dropdownStore={countryDropdownStore} isClearable={true} />
				</div>
				<div className="col col-sml-12 col-med-6">
					<BaasicDropdown dropdownStore={stateDropdownStore} isClearable={true} />
				</div>
				<div className="col col-sml-12 col-med-6">
					<BaasicDropdown dropdownStore={timezoneDropdownStore} isClearable={true} />
				</div>
			</div>
		</div>
	);
});

const WarehouseAdditionalInfoSection = observer(({ viewStore, t }) => {
	const {
		form,
		onTimeChange,
		inventoryLevelDropdownStore,
		warehouseStatusDropdownStore,
		preferredLtlCarrierDropdownStore,
		setRecipientsValue,
	} = viewStore;
	return (
		<div className="spc--bottom--sml">
			<h2 className="spc--bottom--sml">{t('ORGANIZATION.ADDITIONALINFO.TITLE')}</h2>
			<div className="row">
				<div className="col col-sml-12 col-med-6 spc--bottom--sml">
					<BaasicDropdown dropdownStore={warehouseStatusDropdownStore} />
				</div>
				<div className="col col-sml-12 col-med-6">
					<BaasicTimeInput
						field={form.$('cutoffTime')}
						onValueChange={value => onTimeChange(value, 'cutoffTime')}
						format="##:##"
						placeholderFormat="HH:mm"
						mask={['H', 'H', 'm', 'm']}
					/>
				</div>
				<div className="col col-sml-12 col-med-6 spc--bottom--sml">
					<BaasicDropdown dropdownStore={inventoryLevelDropdownStore} />
				</div>
				<div className="col col-sml-12 col-med-6 spc--bottom--sml">
					<BaasicDropdown dropdownStore={preferredLtlCarrierDropdownStore} isClearable={true} />
				</div>
				<div className="col col-sml-12 col-lrg-6 spc--bottom--sml">
					<CustomCreateableSelect
						onChange={setRecipientsValue}
						value={form.$('warehouseNotificationRecipient').value}
						label={t('ORGANIZATION.CONTACT.WAREHOUSENOTIFICATIONRECIPIENT')}
						format={/^[^\s@]+@[^\s@]+\.[^\s@]+$/}
					/>
				</div>
				<div className="col col-sml-12 col-med-6">
					<div className="spc--bottom--sml">
						<BaasicCheckbox field={form.$('acceptingReturns')} />
					</div>
					<div className="spc--bottom--sml">
						<BaasicCheckbox field={form.$('allowSaturdayShipping')} />
					</div>
				</div>

				<div className="col col-sml-12 col-med-6">
					<div className="spc--bottom--sml">
						<SliderCheckBox field={form.$('enableEmailNotification')} />
					</div>
				</div>
			</div>
		</div>
	);
});

export default defaultTemplate(WarehouseEditTemplate);
